import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PermissionUiService } from './permission-ui.service';
import { PromptService } from '../promtp-service/prompt.service';
import { PermissionDefinitions } from './permission-definitions';

@Injectable({ providedIn: 'root' })
export class SecureApiService {
  private readonly sectionPermissionMap: Record<string, string> = {
    'patients-list': PermissionDefinitions.PATIENT_LIST_VIEW,
    'protocols-list': PermissionDefinitions.PROTOCOL_LIST_VIEW,
  };

  constructor(private permissionUi: PermissionUiService, 
    protected promptService: PromptService,
  ) {}

secureCall<T>(
  sectionId: string,
  apiCall$: Observable<T>,
  deniedMessage?: string
): Observable<T | null> {

  const normalizedSectionId = this.normalizeSectionId(sectionId);

  return apiCall$.pipe(
    tap(() => {
      // Successful call = section is allowed
      this.permissionUi.allow(normalizedSectionId);
    }),
    catchError((error: HttpErrorResponse) => {

      if (error.status === 401 || error.status === 403) {
        this.permissionUi.deny(normalizedSectionId, deniedMessage);
        return of(null);
      }

      // Network / unexpected error
      return from(
        this.promptService.showNetworkConnectionError(
          error,
          'secure-api-service'
        )
      ).pipe(
        switchMap(() => of(null))
      );
    })
  );
}

  private normalizeSectionId(sectionId: string): string {
    return this.sectionPermissionMap[sectionId] ?? sectionId;
  }


  secureCallMessage<T>(
    apiCall$: Observable<T>,
    deniedMessage?: string
  ): Observable<T | null> {

    return apiCall$.pipe(
      tap(() => {
        // Successful call = section is allowed
      }),
      catchError((error: HttpErrorResponse) => {

        if (error.status === 401 || error.status === 403) {
          this.promptService.showError(
            deniedMessage || 'You are not authorized to perform this action.'
          );
          return of(null);
        }

        return from(
          this.promptService.showNetworkConnectionError(
            error,
            'secure-api-service'
          )
        ).pipe(map(() => null));
      })
    );
  }

}
